import React, { useState, useEffect } from 'react';
import { CheckCircle, CreditCard, Loader2, X, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { useSocket } from '../context/SocketContext';
import api from '../utils/api';

interface BankDetails {
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  branch: string;
}

interface Driver {
  id: string;
  name: string;
  phone: string;
  email?: string;
  isVerified?: boolean;
  balance?: number;
  bankDetails?: BankDetails;
  registrationDate?: string;
  pendingRequest?: any;
}

const ExistingDrivers = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bankForm, setBankForm] = useState<BankDetails>({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    branch: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedDriverId, setExpandedDriverId] = useState<string | null>(null);
  const [payouts, setPayouts] = useState<any[]>([]);
  const [loadingPayouts, setLoadingPayouts] = useState(false);

  const { socket } = useSocket();

  const fetchDrivers = async () => {
    try {
      const response = await api.get('/drivers/payout-summary');
      if (Array.isArray(response.data)) {
        setDrivers(response.data);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
    if (socket) {
      socket.on('driver_updated', (updatedData: { id: string, bankDetails: BankDetails }) => {
        setDrivers(prev => prev.map(d => d.id === updatedData.id ? { ...d, bankDetails: updatedData.bankDetails } : d));
      });
      socket.on('driver_approved', () => fetchDrivers());
      socket.on('payout_processed', () => fetchDrivers());
    }
    return () => {
      if (socket) {
        socket.off('driver_updated');
        socket.off('driver_approved');
        socket.off('payout_processed');
      }
    };
  }, [socket]);

  const handleAddBankClick = (driver: Driver) => {
    setSelectedDriver(driver);
    setBankForm(driver.bankDetails || { accountHolderName: '', bankName: '', accountNumber: '', branch: '' });
    setIsModalOpen(true);
  };

  const handleBankSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDriver) return;
    setIsSubmitting(true);
    try {
      await api.patch(`/driver/${selectedDriver.id}/bank-details`, { bankDetails: bankForm });
      setIsModalOpen(false);
      fetchDrivers();
    } catch (error) {
      console.error('Error saving bank details:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleAccordion = async (driverId: string) => {
    if (expandedDriverId === driverId) {
      setExpandedDriverId(null);
      setPayouts([]);
    } else {
      setExpandedDriverId(driverId);
      setLoadingPayouts(true);
      try {
        const response = await api.get(`/driver/${driverId}/payouts`);
        setPayouts(response.data);
      } catch (error) {
        console.error('Error fetching payouts:', error);
      } finally {
        setLoadingPayouts(false);
      }
    }
  };

  const validateForm = () => {
    const charsOnly = /^[a-zA-Z\s]*$/;
    const numsOnly = /^[0-9]*$/;
    return (
      bankForm.accountHolderName.trim() !== '' &&
      bankForm.bankName.trim() !== '' &&
      bankForm.accountNumber.trim() !== '' &&
      bankForm.branch.trim() !== '' &&
      charsOnly.test(bankForm.accountHolderName) &&
      charsOnly.test(bankForm.bankName) &&
      charsOnly.test(bankForm.branch) &&
      numsOnly.test(bankForm.accountNumber)
    );
  };

  const filteredDrivers = drivers.filter(d => {
    const s = search.toLowerCase().trim();
    return d.name.toLowerCase().includes(s) || d.phone.includes(s);
  });

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-20 flex flex-col items-center justify-center border border-gray-100 shadow-sm">
        <Loader2 className="w-10 h-10 text-[#05A664] animate-spin mb-4" />
        <p className="text-[#909090] font-medium">Loading drivers...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[2rem] p-10 border border-gray-100 shadow-sm">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#F1F8F5] rounded-2xl flex items-center justify-center shadow-inner">
            <CheckCircle className="text-[#05A664] w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-[#121415]">Existing Drivers</h2>
            <p className="text-sm text-[#909090] font-medium">Manage and view all verified driver accounts</p>
          </div>
        </div>

        <div className="relative group flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-[#05A664] transition-colors" />
          <input
            type="text"
            placeholder="Search driver by name or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-6 py-3.5 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:border-[#05A664] focus:ring-4 focus:ring-[#05A664]/5 outline-none transition-all font-medium text-sm"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#F1F8F5]">
              <th className="px-8 py-4 text-[10px] font-black text-[#909090] uppercase tracking-[0.2em] border-b border-gray-100">Driver Name</th>
              <th className="px-8 py-4 text-[10px] font-black text-[#909090] uppercase tracking-[0.2em] border-b border-gray-100">Contact Info</th>
              <th className="px-8 py-4 text-[10px] font-black text-[#909090] uppercase tracking-[0.2em] border-b border-gray-100">Current Balance</th>
              <th className="px-8 py-4 text-[10px] font-black text-[#909090] uppercase tracking-[0.2em] border-b border-gray-100">Payment Status</th>
              <th className="px-8 py-4 text-[10px] font-black text-[#909090] uppercase tracking-[0.2em] border-b border-gray-100 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredDrivers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-8 py-20 text-center text-[#909090] font-medium">
                  {search ? `No drivers found matching "${search}"` : "No verified drivers found."}
                </td>
              </tr>
            ) : (
              filteredDrivers.map((driver) => (
                <React.Fragment key={driver.id}>
                  <tr 
                    onClick={() => toggleAccordion(driver.id)}
                    className={`cursor-pointer transition-colors group ${expandedDriverId === driver.id ? 'bg-[#F1F8F5]/50' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-8 py-6 font-bold text-[#121415]">{driver.name}</td>
                    <td className="px-8 py-6">
                      <div className="text-sm font-medium text-[#121415]">{driver.phone}</div>
                      <div className="text-[10px] text-[#909090]">{driver.email || 'No email'}</div>
                    </td>
                    <td className="px-8 py-6 font-black text-[#121415]">
                      Rs {(driver.balance || 0).toLocaleString()}
                    </td>
                    <td className="px-8 py-6">
                      {driver.pendingRequest ? (
                        <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-3 py-1.5 rounded-xl flex items-center gap-1.5 w-fit">
                          <div className="w-1.5 h-1.5 bg-orange-600 rounded-full animate-pulse"></div>
                          Pending
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-[#05A664] bg-[#E8F5EE] px-3 py-1.5 rounded-xl flex items-center gap-1.5 w-fit">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Settled
                        </span>
                      )}
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddBankClick(driver);
                          }}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border border-gray-100 text-[#121415] hover:bg-white hover:border-[#05A664] hover:text-[#05A664] transition-all shadow-sm"
                        >
                          <CreditCard className="w-4 h-4" />
                          Edit Bank
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAccordion(driver.id);
                          }}
                          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all shadow-sm ${
                            expandedDriverId === driver.id 
                            ? 'bg-[#121415] text-white border-[#121415]' 
                            : 'bg-[#F1F8F5] text-[#05A664] border-transparent hover:bg-[#05A664] hover:text-white'
                          } border`}
                        >
                          {expandedDriverId === driver.id ? 'Close' : 'History'}
                          {expandedDriverId === driver.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedDriverId === driver.id && (
                    <tr className="bg-gray-50/50">
                      <td colSpan={4} className="px-8 py-8">
                        <div className="animate-in slide-in-from-top-2 duration-300">
                          <div className="flex items-center justify-between mb-6">
                            <h3 className="text-sm font-black text-[#121415] uppercase tracking-wider flex items-center gap-2">
                              <div className="w-1.5 h-4 bg-[#05A664] rounded-full"></div>
                              Recent Payouts
                            </h3>
                          </div>
                          {loadingPayouts ? (
                            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#05A664]" /></div>
                          ) : payouts.length === 0 ? (
                            <p className="text-xs text-[#909090] italic">No payout history available.</p>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {payouts.map(p => (
                                <div key={p.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex justify-between items-center">
                                  <div>
                                    <p className="text-[10px] font-bold text-[#909090] uppercase tracking-wider">{new Date(p.requestedAt).toLocaleDateString()}</p>
                                    <p className="text-sm font-black text-[#121415]">Rs {p.amount.toLocaleString()}</p>
                                  </div>
                                  <span className={`text-[9px] font-black px-2 py-0.5 rounded-md ${p.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                    {p.status.toUpperCase()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#121415]/40 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md overflow-hidden shadow-2xl">
            <div className="px-8 pt-8 pb-4 border-b border-gray-50 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-[#121415]">Bank Details</h3>
                <p className="text-xs text-[#909090] font-medium">Update account for {selectedDriver?.name}</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5 text-[#909090]" /></button>
            </div>
            <form onSubmit={handleBankSubmit} className="p-8 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-[#121415] uppercase tracking-wider ml-1">Account Holder's Name</label>
                <input required type="text" placeholder="Full Name" className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-[#05A664]/20" value={bankForm.accountHolderName} onChange={e => setBankForm({...bankForm, accountHolderName: e.target.value})} />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-[#121415] uppercase tracking-wider ml-1">Bank Name</label>
                <input required type="text" placeholder="e.g. Bank of Ceylon" className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-[#05A664]/20" value={bankForm.bankName} onChange={e => setBankForm({...bankForm, bankName: e.target.value})} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-[#121415] uppercase tracking-wider ml-1">Account Number</label>
                  <input required type="text" placeholder="0000 0000 0000" className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-[#05A664]/20" value={bankForm.accountNumber} onChange={e => setBankForm({...bankForm, accountNumber: e.target.value})} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-[#121415] uppercase tracking-wider ml-1">Branch</label>
                  <input required type="text" placeholder="Branch Name" className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-[#05A664]/20" value={bankForm.branch} onChange={e => setBankForm({...bankForm, branch: e.target.value})} />
                </div>
              </div>
              <button disabled={isSubmitting || !validateForm()} type="submit" className="w-full py-4 bg-[#05A664] text-white rounded-2xl font-bold text-sm shadow-lg hover:bg-black transition-all disabled:bg-gray-100 disabled:text-gray-400">
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save Bank Details'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExistingDrivers;
